import styled, { css } from "styled-components";
import type { CheckboxVariantsType } from "./Checkbox";

// STYLES
const checkboxStyle = css`
    --_checkbox-width: var(--checkbox-width, 40px);
    --_checkbox-height: var(--checkbox-height, 40px);
    --_checkbox-border-radius: var(--checkbox-border-radius, 4px);
    --_checkbox-text-color: var(--checkbox-text-color, --color-primary-black);
    --_checkbox-bg-color: var(--checkbox-bg-color, --color-primary-white);
    --_checkbox-border-color: var(--checkbox-border-color, --color-grey-4);
    --_checkbox-hover-text-color: var(--checkbox-hover-text-color, --color-primary-black);
    --_checkbox-hover-bg-color: var(--checkbox-hover-bg-color, --color-primary-black);
    --_checkbox-hover-border-color: var(--checkbox-hover-border-color, --color-primary-black);
    --_checkbox-active-text-color: var(--checkbox-active-text-color, --color-primary-white);
    --_checkbox-active-bg-color: var(--checkbox-active-bg-color, --color-primary-black);
    --_checkbox-active-border-color: var(--checkbox-active-border-color, --color-primary-black);
    --_checkbox-disabled-text-color: var(--checkbox-disabled-text-color, --color-primary-black);
    --_checkbox-disabled-bg-color: var(--checkbox-disabled-bg-color, --color-primary-black);
    --_checkbox-disabled-border-color: var(--checkbox-disabled-border-color, --color-primary-black);
    position: relative;
    width: var(--_checkbox-width);
    height: var(--_checkbox-height);
    border-radius: var(--_checkbox-border-radius);

    label {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--_checkbox-width);
        height: var(--_checkbox-height);
        margin: 0;
        color: var(--_checkbox-text-color);
        background-color: var(--_checkbox-bg-color);
        border: 1px solid var(--_checkbox-border-color);
        border-radius: var(--_checkbox-border-radius);
        cursor: pointer;
    }

    input[type="checkbox"] {
        position: absolute;
        white-space: nowrap;
        cursor: pointer;
        opacity: 0;
        overflow: hidden;
    }

    input[type="checkbox"]:checked ~ label {
        color: var(--_checkbox-active-text-color);
        background-color: var(--_checkbox-active-bg-color);
        border-color: var(--_checkbox-active-border-color);
    }

    input[disabled]:checked ~ label,
    input[disabled] ~ label {
        color: var(--_checkbox-disabled-text-color);
        background-color: var(--_checkbox-disabled-bg-color);
        border-color: var(--_checkbox-disabled-border-color);
        cursor: default;
    }

    label:hover,
    input[type="checkbox"]:hover + label {
        color: var(--_checkbox-hover-text-color);
        background-color: var(--_checkbox-hover-bg-color);
        border-color: var(--_checkbox-hover-border-color);
    }

    &:focus-within {
        label {
            color: var(--_checkbox-hover-text-color);
            background-color: var(--_checkbox-hover-bg-color);
            border-color: var(--_checkbox-hover-border-color);
        }
    }
`;

const favourite = css`
    --checkbox-width: 40px;
    --checkbox-height: 40px;
    --checkbox-border-radius: 50%;
    --checkbox-text-color: var(--color-primary-black);
    --checkbox-bg-color: var(--color-primary-white);
    --checkbox-border-color: var(--color-grey-4);
    --checkbox-hover-text-color: var(--color-highlight-black);
    --checkbox-hover-bg-color: var(--color-primary-white);
    --checkbox-hover-border-color: var(--color-highlight-black);
    --checkbox-active-text-color: var(--color-primary-white);
    --checkbox-active-bg-color: var(--color-primary-black);
    --checkbox-active-border-color: var(--color-primary-black);
    --checkbox-disabled-text-color: var(--color-primary-black);
    --checkbox-disabled-bg-color: var(--color-primary-white);
    --checkbox-disabled-border-color: var(--color-grey-4);
    ${checkboxStyle};
`;

const pack = css`
    --checkbox-width: 32px;
    --checkbox-height: 32px;
    --checkbox-border-radius: 4px;
    --checkbox-text-color: var(--color-primary-black);
    --checkbox-bg-color: var(--color-primary-white);
    --checkbox-border-color: var(--color-primary-black);
    --checkbox-hover-text-color: var(--color-primary-white);
    --checkbox-hover-bg-color: var(--color-primary-black);
    --checkbox-hover-border-color: var(--color-primary-black);
    --checkbox-active-text-color: var(--color-primary-white);
    --checkbox-active-bg-color: var(--color-primary-black);
    --checkbox-active-border-color: var(--color-primary-black);
    --checkbox-disabled-text-color: var(--color-primary-white);
    --checkbox-disabled-bg-color: var(--color-grey-6);
    --checkbox-disabled-border-color: var(--color-grey-6);
    ${checkboxStyle};
`;

const form = css`
    display: flex;
    flex-direction: column;
    row-gap: var(--spacing-1);
    margin: var(--spacing-1) 0;

    & > div {
        display: flex;
        padding: calc(var(--spacing-1) / 2) 0;
    }

    a {
        margin-inline-start: var(--spacing-1);
        font-family: var(--font-book);
        font-size: 1.3rem;
        line-height: 2rem;
        text-decoration: underline;
        color: var(--color-primary-black);

        @supports not (margin-inline-start: 0) {
            margin-left: var(--spacing-1);
        }
    }

    label {
        display: inline-flex;
        position: relative;
        align-items: center;
        margin: 0;
        padding-inline-start: 36px; /* 20 (width checkbox) + 16 (spacing) */
        font-family: var(--font-book);
        font-size: 1.6rem;
        line-height: 1.5;
        color: var(--color-primary-black);
        cursor: pointer;

        &::before {
            display: block;
            position: absolute;
            top: 2px;
            inset-inline-start: 0;
            width: 20px;
            height: 20px;
            content: "";
            border: 1px solid var(--color-grey-6);
            border-radius: 4px;
            cursor: pointer;
        }

        /* Not supported in chrome 56 (Tizen v4.0) */
        @supports not (padding-inline: 0) {
            padding-left: 36px; /* 20 (width checkbox) + 16 (spacing) */
        }

        /* Not supported in chrome 85 (Tizen v6.5) */
        @supports not (inset-inline: 0) {
            &::before {
                left: 0;
            }
        }
    }

    input[type="checkbox"] {
        position: absolute;
        width: 1px;
        height: 1px;
        clip: rect(0 0 0 0);
        clip-path: inset(50%);
        white-space: nowrap;
        overflow: hidden;
    }

    input:checked + label::before {
        background-color: var(--color-primary-black);
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='%23fff' stroke-width='1.5' d='m6.364 10.303 2.666 2.666M7.97 12.97l6-6'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        border-color: var(--color-primary-black);
    }

    input:disabled + label {
        color: var(--color-grey-6);
        cursor: default;
        &::before {
            background-color: var(--color-grey-7);
            border-color: var(--color-grey-7);
            cursor: default;
        }
    }

    @media (hover: hover) {
        &:hover {
            label::before {
                background-color: var(--color-grey-6);
            }
        }
    }

    &:focus-within {
        label::before {
            outline: 2px solid var(--color-blue);
        }
    }
`;

// COMPONENTS
export const CheckboxVariants = {
    favourite,
    pack,
    form,
};

const CheckboxVariant = styled.div<{ variant: CheckboxVariantsType }>`
    ${({ variant }) =>
        variant &&
        css`
            ${CheckboxVariants[variant]};
        `};
`;

export default CheckboxVariant;
